import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { PortfolioItem } from '../types/content';
type PortfolioGridProps = {
  items: PortfolioItem[];
};
export function PortfolioGrid({
  items
}: PortfolioGridProps) {
  const [active, setActive] = useState<number | null>(null);
  const close = useCallback(() => setActive(null), []);
  const step = useCallback((dir: 1 | -1) => setActive(i => i === null ? i : (i + dir + items.length) % items.length), [items.length]);
  useEffect(() => {
    if (active === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [active, close, step]);
  const current = active !== null ? items[active] : null;
  return <>
      <ul className="grid grid-cols-2 gap-[clamp(6px,.6vw,14px)] sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 min-[2600px]:grid-cols-8" data-id="element-243" data-mp="2f7sqmovxkktu">
        {items.map((item, index) => <li key={item.id} data-id="element-244" data-mp="267noou9iaen2" data-mpi={index}>
            <button type="button" onClick={() => setActive(index)} className="group relative block aspect-square w-full overflow-hidden rounded-[12px] border border-ink-line bg-ink-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neon" aria-label={`View ${item.title}`} data-id="element-245" data-mp="1rkcbknxoemku">
              <img src={item.image} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-246" data-mp="18lgi5etn6c82" />
              <span className="absolute inset-0 flex items-end bg-ink/0 p-4 text-left opacity-0 transition-[opacity,background-color] duration-200 ease-out group-hover:bg-ink/70 group-hover:opacity-100 group-focus-visible:bg-ink/70 group-focus-visible:opacity-100" data-id="element-247" data-mp="1djrv3yf8h2ui">
                <span data-id="element-248" data-mp="kr6e85cheevl">
                  <span className="block font-slab text-lg font-black uppercase leading-snug text-white" data-id="element-249" data-mp="kqeimuivwh5b">{item.title}</span>
                  {item.artist && <span className="mt-1 block font-script text-sm italic text-gold-2" data-id="element-250" data-mp="1deiqnmoxh6b6">by {item.artist}</span>}
                </span>
              </span>
            </button>
          </li>)}
      </ul>

      <AnimatePresence data-id="element-251" data-mp="13g3a2dnj1ffx">
        {current && <motion.div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/95 p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} role="dialog" aria-modal="true" aria-label={current.title} onClick={close} data-id="element-252" data-mp="13eru7fgt4h8g">
            <button type="button" onClick={close} className="absolute right-4 top-4 p-2 hover:opacity-60" aria-label="Close" data-id="element-253" data-mp="23osw9w766mg3">
              <XIcon className="h-7 w-7" strokeWidth={1.5} data-id="element-254" data-mp="28ne0s7tgfsac" />
            </button>
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(-1);
        }} className="absolute left-2 top-1/2 -translate-y-1/2 p-3 hover:opacity-60 md:left-6" aria-label="Previous" data-id="element-255" data-mp="2decfx42pikc0">
              <ChevronLeftIcon className="h-8 w-8" strokeWidth={1.5} data-id="element-256" data-mp="2rdvnnvrlvtns" />
            </button>
            <motion.figure key={current.id} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-[min(92vw,1400px)]" onClick={e => e.stopPropagation()} data-id="element-257" data-mp="2wd8a4fekeypu">
              <img src={current.image} alt={current.title} className="max-h-[78vh] w-auto rounded-[12px] object-contain" data-id="element-258" data-mp="2sezt7txsyoft" />
              <figcaption className="mt-4 text-center" data-id="element-259" data-mp="2njkg547mcwkg">
                <span className="block font-slab text-xl font-black uppercase text-white" data-id="element-260" data-mp="31g7qc8ol1j8b">{current.title}</span>
                <span className="mt-1 block text-sm text-ink-muted" data-id="element-261" data-mp="2xgyw72pmrjc8">
                  {current.artist ? `By ${current.artist} · ` : ''}Trip Ink Tattoo Las Vegas · {active! + 1} / {items.length}
                </span>
              </figcaption>
            </motion.figure>
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(1);
        }} className="absolute right-2 top-1/2 -translate-y-1/2 p-3 hover:opacity-60 md:right-6" aria-label="Next" data-id="element-262" data-mp="2iiwuky25t7xm">
              <ChevronRightIcon className="h-8 w-8" strokeWidth={1.5} data-id="element-263" data-mp="1m1a98bcdmkbf" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </>;
}